import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { generatePage, getMyPage } from '../services/api';
import { Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthVerifyPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState(() => token ? 'verifying' : 'error');
  const [error, setError] = useState(() => token ? '' : 'No token provided');
  const [message, setMessage] = useState('Verifying your link...');
  const hasVerified = useRef(false);

  useEffect(() => {
    if (!token || hasVerified.current) return;
    
    hasVerified.current = true;
    
    const verifyToken = async () => {
      try {
        // Call verify endpoint directly to get pending_page data
        const response = await axios.post(`${BACKEND_URL}/api/auth/verify`, { token });
        const { token: jwtToken, user, pending_page } = response.data;
        
        // Login the user
        await login(jwtToken);
        
        // Check if there's pending page data from the database
        if (pending_page && pending_page.upload_id) {
          setStatus('generating');
          setMessage('Creating your page...');
          
          try {
            // Generate the page using the pending data
            await generatePage(pending_page.upload_id, pending_page.answers || {});
            
            setStatus('success');
            setMessage('Your page is ready!');
            toast.success('Your page has been created!');
            
            setTimeout(() => {
              navigate('/editor');
            }, 1500);
            return;
          } catch (genError) {
            console.error('Error generating page:', genError);
            // Continue to editor anyway - they can create from scratch
            toast.error('Could not generate page automatically');
          }
        }
        
        // No pending page data - check if user has a page
        // Admin users always go to admin dashboard
        if (user?.is_admin) {
          setStatus('success');
          setMessage('Welcome back, admin!');
          setTimeout(() => {
            navigate('/admin');
          }, 1500);
          return;
        }
        
        // Check if regular user has a page
        try {
          const myPage = await getMyPage();
          if (myPage) {
            // User has a page, go to editor
            setStatus('success');
            setMessage('You\'re signed in!');
            setTimeout(() => {
              navigate('/editor');
            }, 1500);
          } else {
            // User doesn't have a page - redirect to waitlist
            setStatus('success');
            setMessage('Thanks for your interest!');
            toast.info('You\'re on the waitlist. We\'ll reach out soon!');
            setTimeout(() => {
              navigate('/waitlist');
            }, 1500);
          }
        } catch (pageErr) {
          // Error fetching page likely means no page exists - send to waitlist
          setStatus('success');
          setMessage('Thanks for your interest!');
          toast.info('You\'re on the waitlist. We\'ll reach out soon!');
          setTimeout(() => {
            navigate('/waitlist');
          }, 1500);
        }
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.detail || 'Invalid or expired link');
      }
    };
    
    verifyToken();
  }, [token, login, navigate]);

  return (
    <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center px-6">
      <div className="text-center animate-fade-in">
        {(status === 'verifying' || status === 'generating') && (
          <>
            <div className="w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 animate-spin text-[#1A1918]" />
            </div>
            <h1 className="font-heading text-2xl font-medium text-[#1A1918] mb-2">
              {message}
            </h1>
            <p className="text-[#666460]">Please wait a moment</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-heading text-2xl font-medium text-[#1A1918] mb-2">
              {message}
            </h1>
            <p className="text-[#666460]">Redirecting...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <X className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="font-heading text-2xl font-medium text-[#1A1918] mb-2">
              Link expired or invalid
            </h1>
            <p className="text-[#666460] mb-6">{error}</p>
            <a href="/login" className="text-[#1A1918] underline">
              Request a new link
            </a>
          </>
        )}
      </div>
    </div>
  );
}
